import { useEffect, useId, useRef, useState } from 'react'
import { useSvgData } from '~/components/Media/hooks/useSvgData'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import Image from '~/components/Helpers/Image'
import BlockVideo from '~/components/Media/components/BlockVideo'
import MarkDown from '~/components/Helpers/MarkDown'

const blockStyles = cva('', {
  variants: {
    parent: {
      'position-1': 'items-end w-[calc(100%-20px)] sm:w-[calc(100%-32px)]',
      'position-2': '-mt-[31px] w-full',
      'position-3': 'items-start w-[calc(100%-20px)] sm:w-[calc(100%-32px)]',
      'position-4': '-mb-[31px] w-full'
    },
    svg: {
      'position-1': 'w-[calc(100%+20px)] sm:w-[calc(100%+32px)] h-full', // left
      'position-2': 'h-[calc(100%+20px)] sm:h-[calc(100%+32px)] w-full', // top
      'position-3': 'w-[calc(100%+20px)] sm:w-[calc(100%+32px)] h-full', // right
      'position-4': 'h-[calc(100%+20px)] sm:h-[calc(100%+32px)] w-full' // bottom
    },
    aspect: {
      square: 'aspect-square',
      landscape: 'aspect-3/2'
    }
  },
  defaultVariants: {
    parent: 'position-2',
    aspect: 'square'
  }
})

type TestProps = {
  image: Image
  video?: Asset
  aspectRatio: 'square' | 'landscape'
  brandDevicePosition: 'position-1' | 'position-2' | 'position-3' | 'position-4'
  brandDeviceColour?: string
}

const ImageBlock = ({
  content,
  size,
  className
}: {
  content: TestProps
  size: number
  className?: string
}) => {
  const { image, video, aspectRatio, brandDevicePosition } = content
  const [sizeNum, setSizeNum] = useState(size)
  const id = useId()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateSize = () => {
      const width = containerRef.current?.offsetWidth || window.innerWidth
      if (window.innerWidth < 640) {
        setSizeNum(20)
      } else if (width < 414) {
        setSizeNum(24)
      } else {
        setSizeNum(32)
      }
    }

    updateSize()
    window.addEventListener('resize', updateSize)

    return () => window.removeEventListener('resize', updateSize)
  }, [])

  const rectData = useSvgData(brandDevicePosition, sizeNum || 32)

  // Check if transformations aren't null and gets width from w= or leave blank
  const imgWidth = '910'

  // Check if transformations aren't null and gets height from h= or leave blank
  const imgHeight = '910'

  const maskId = `shape-${brandDevicePosition}-${id}`

  return (
    <div className='@container/imageblock w-full'>
      <div
        ref={containerRef}
        className={cn(
          blockStyles({
            parent: brandDevicePosition,
            svg: null,
            aspect: aspectRatio
          }),
          image?.caption && brandDevicePosition === 'position-4'
            ? 'bg-grey-l mb-0'
            : '',
          '@container/imageblock flex max-w-[56.625rem] flex-col',
          '@min-[57rem]/imageblock:mx-auto',
          className
        )}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className={cn(
            blockStyles({ parent: null, svg: brandDevicePosition }),
            'object-cover'
          )}
        >
          <mask id={maskId}>
            <rect fill='white' x='0' y='0' width={`100%`} height={`100%`} />

            {rectData.map((rect, i: Index) => (
              <rect
                key={i}
                x={rect.x || 0}
                y={rect.y || 0}
                width={rect.width || 0}
                height={rect.height || 0}
                style={rect.style}
              />
            ))}
          </mask>
          <foreignObject width='100%' height='100%' mask={`url(#${maskId})`}>
            {video ? (
              <div className='relative h-full w-full object-cover'>
                <BlockVideo
                  videoUrl={video.sys.uri}
                  width={imgWidth}
                  height={imgHeight}
                  image={image}
                  btnPosition={
                    brandDevicePosition === 'position-4'
                      ? 'top-right'
                      : 'bottom-right'
                  }
                  btnClassName={
                    brandDevicePosition === 'position-4' ? '' : 'right-xl'
                  }
                />
              </div>
            ) : (
              <Image image={image} />
            )}
          </foreignObject>
        </svg>
        {image?.caption && (
          <div className='bg-grey-l p-xs-plus text-base-lg text-blue-d -mt-[1px] w-full italic'>
            <MarkDown content={image?.caption} />
          </div>
        )}
      </div>
    </div>
  )
}

export default ImageBlock
